import React, { useState, useEffect } from 'react';
import { 
  Smartphone, 
  Plus, 
  RefreshCw,
  QrCode,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings
} from 'lucide-react';
import { Button, Card, Badge, Loading } from '../../components/common';
import tenantsService from '../../services/api/tenants.service';
import CreateInstanceModal from '../../components/dashboard/CreateInstanceModal/CreateInstanceModal';
import QRCodeModal from '../../components/dashboard/QRCodeModal/QRCodeModal';

const EvolutionInstances = () => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);

  useEffect(() => {
    loadInstances();
  }, []);

  const loadInstances = async () => {
    try {
      setLoading(true);
      const data = await tenantsService.listEvolutionInstances();
      setInstances(data.instances || []);
    } catch (error) {
      console.error('Erro ao carregar instâncias:', error);
      if (error.error === 'Evolution API não configurada') {
        alert('Evolution API não está configurada. Configure EVOLUTION_API_URL e EVOLUTION_API_KEY nas variáveis de ambiente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCreateModalOpen(true);
  };

  const handleShowQRCode = async (instanceName) => {
    try {
      const data = await tenantsService.getEvolutionQRCode(instanceName);
      setSelectedInstance({ name: instanceName, qrcode: data.qrcode });
      setQrModalOpen(true);
    } catch (error) {
      alert('Erro ao obter QR Code: ' + error.message);
    }
  };

  const handleDelete = async (instanceName) => {
    if (!window.confirm(`Tem certeza que deseja deletar a instância "${instanceName}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await tenantsService.deleteEvolutionInstance(instanceName);
      alert('Instância deletada com sucesso!');
      loadInstances();
    } catch (error) {
      alert('Erro ao deletar instância: ' + error.message);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge variant="success" dot>Conectado</Badge>;
      case 'connecting':
        return <Badge variant="warning" dot>Conectando</Badge>;
      case 'close':
        return <Badge variant="danger" dot>Desconectado</Badge>;
      default:
        return <Badge variant="secondary">Desconhecido</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'connecting':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <XCircle className="w-5 h-5 text-red-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Carregando instâncias..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Smartphone className="w-7 h-7 text-primary-500" />
            Instâncias Evolution API
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie instâncias do WhatsApp via Evolution API
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="md"
            onClick={loadInstances}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Atualizar
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleCreate}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Nova Instância
          </Button>
        </div>
      </div>

      {/* Instances Grid */}
      {instances.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-12">
            <Smartphone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhuma instância cadastrada
            </h3>
            <p className="text-gray-600 mb-4">
              Crie sua primeira instância Evolution API
            </p>
            <Button
              variant="primary"
              onClick={handleCreate}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              Criar Primeira Instância
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {instances.map((instance) => (
            <Card key={instance.instanceName} hoverable>
              <Card.Body>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                      {instance.instanceName}
                    </h3>
                    <p className="text-xs text-gray-500">{instance.integration || 'WHATSAPP-BAILEYS'}</p>
                  </div>
                  
                  {getStatusBadge(instance.status || instance.state)}
                </div>

                {/* Status */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
                  {getStatusIcon(instance.status || instance.state)}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {instance.status === 'open' || instance.state === 'open' ? 'WhatsApp Conectado' : 'WhatsApp Desconectado'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {instance.status === 'open' || instance.state === 'open' 
                        ? 'Pronto para receber mensagens' 
                        : 'Escaneie o QR Code para conectar'}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {(instance.status !== 'open' && instance.state !== 'open') && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleShowQRCode(instance.instanceName)}
                      leftIcon={<QrCode className="w-4 h-4" />}
                    >
                      QR Code
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShowQRCode(instance.instanceName)}
                    leftIcon={<Settings className="w-4 h-4" />}
                  >
                    Detalhes
                  </Button>

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(instance.instanceName)}
                    leftIcon={<Trash2 className="w-4 h-4" />}
                  >
                    Deletar
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      {createModalOpen && (
        <CreateInstanceModal
          onClose={() => setCreateModalOpen(false)}
          onSuccess={() => {
            setCreateModalOpen(false);
            loadInstances();
          }}
        />
      )}

      {qrModalOpen && selectedInstance && (
        <QRCodeModal
          instance={selectedInstance}
          onClose={() => {
            setQrModalOpen(false);
            setSelectedInstance(null);
          }}
          onConnected={loadInstances}
        />
      )}
    </div>
  );
};

export default EvolutionInstances;